// localStorage round-trip + boot validation + write-through + quota
// fallback for the app's data layer. Per spec/tech-arch.md §5.1 + §5.2
// + §5.3 + §12.2 + spec/build-plan.md P-021 row + P-035 form-UX-polish
// signal extension.
//
// **Boot read flow per Tech Arch §5.1:**
//   1. localStorage.getItem(KEY) — null on no saved data (clean state)
//   2. JSON.parse — throws on malformed JSON → parse failure
//   3. Inspect schemaVersion — must be a number ≤ CURRENT (else fail)
//   4. Run migrations if savedVersion < CURRENT
//   5. AppStateSchema.parse — throws on shape mismatch
//
// **Future-version handling.** `schemaVersion > CURRENT_SCHEMA_VERSION`
// means the data was written by a later build. Treat as parse failure;
// do NOT auto-clear or downgrade — recovery is user-driven (switch back
// to the newer build, download backup JSON via P-034, OR DevTools clear).
//
// **Quota fallback per §5.3.** setItem in try/catch. QuotaExceededError
// (and any other write failure — security errors, etc.) surfaces the
// quota banner; the recovery path (free space, download recent emails)
// is the closest match across failure modes.
//
// **Persistence streams (P-035 extension).** Three module-scope
// subscribables consumed by the form-UX-polish surface:
// - `errorSubscribable: string | null` — persistence-error stream
//   (D-083). Drives `<PersistenceBanner />`.
// - `savedSubscribable: number | null` — `lastSavedAt` ms timestamp.
//   Set after a successful localStorage write; reset to null on a
//   write failure (banner-surfaced separately). Drives
//   `<SavedIndicator />` in the screen header.
// - `pendingSubscribable: 0 | 1` — pending-writes flag. Flips to 1 on
//   any store change (subscriber callback) before the debounced write
//   fires; flips to 0 when the write succeeds. Drives
//   `<DirtyAsterisk />` in the screen header. Boolean-typed-as-int
//   shape leaves headroom for a future multi-write coalesced counter.
//
// All three streams use the shared `createSubscribable<T>()` factory
// (P-035, src/lib/subscribable.ts) — extracted at the 3rd consumer per
// the D-083 forward note + OC §3.3 refactor review.
//
// **Write-through wiring is opt-in.** Tests pass `createStore(...)`
// isolated stores; production calls `attachPersistence(store)` once at
// boot from main.tsx (P-022). Calling at module-load would wire
// localStorage to every test importing the global store. Implementation
// is the subscribe-listener pattern (NOT setState monkey-patch) — keeps
// setState pure and lets persistence detach cleanly.

import { AppStateSchema, CURRENT_SCHEMA_VERSION, type AppState } from './schemas'
import type { Store } from './store'
import { runMigrations } from './migrations'
import { debounce } from '../lib/debounce'
import { createSubscribable } from '../lib/subscribable'
import { useSubscribed } from '../lib/use-subscribed'

export const KEY = 's7-email-builder.v1'

export const PARSE_ERROR_MESSAGE =
  "Saved data couldn't be loaded — import a backup JSON to continue."

export const QUOTA_ERROR_MESSAGE =
  'Local storage full — download recent Emails as JSON and remove old ones from this app to free space.'

const DEBOUNCE_MS = 500

const errorSubscribable = createSubscribable<string | null>(null)
const savedSubscribable = createSubscribable<number | null>(null)
const pendingSubscribable = createSubscribable<0 | 1>(0)

/**
 * Set the current persistence-error state and notify listeners.
 * No-op when the error matches the current value (avoids spurious
 * re-renders downstream).
 */
export function setPersistenceError(error: string | null): void {
  errorSubscribable.setValue(error)
}

/**
 * Subscribe to persistence-error changes. Returns an unsubscribe
 * function. Mirrors `Store.subscribe`. Used by `usePersistenceError`
 * internally + tests + non-Preact consumers.
 */
export function subscribePersistenceError(
  listener: (error: string | null) => void,
): () => void {
  return errorSubscribable.subscribe(listener)
}

/**
 * Preact hook returning the current persistence-error string (or null).
 * Consumed by `<PersistenceBanner />`.
 */
export function usePersistenceError(): string | null {
  return useSubscribed(errorSubscribable.getValue, errorSubscribable.subscribe)
}

/**
 * Read the most recent successful-save timestamp (ms). Returns null
 * when no save has succeeded yet OR after a write failure clears the
 * value. Exported imperatively for tests + future non-Preact consumers.
 */
export function getLastSavedAt(): number | null {
  return savedSubscribable.getValue()
}

/** Subscribe to changes in the lastSavedAt timestamp. */
export function subscribeSaved(listener: (ts: number | null) => void): () => void {
  return savedSubscribable.subscribe(listener)
}

/** Preact hook for the lastSavedAt timestamp. */
export function useLastSavedAt(): number | null {
  return useSubscribed(savedSubscribable.getValue, savedSubscribable.subscribe)
}

/**
 * Read the pending-writes flag (0 = idle, 1 = unwritten changes pending
 * the debounced write).
 */
export function getPendingWrites(): 0 | 1 {
  return pendingSubscribable.getValue()
}

/** Subscribe to changes in the pending-writes flag. */
export function subscribePending(listener: (flag: 0 | 1) => void): () => void {
  return pendingSubscribable.subscribe(listener)
}

/** Preact hook for the pending-writes flag. */
export function usePendingWrites(): 0 | 1 {
  return useSubscribed(pendingSubscribable.getValue, pendingSubscribable.subscribe)
}

/**
 * Read + parse + migrate + validate the persisted AppState. Empty
 * localStorage returns `{state: null, error: null}` (not an error).
 * Any failure returns `{state: null, error: msg}` AND calls
 * `setPersistenceError` so P-022's boot can pipe the banner with one
 * call site. The return value is for tests + programmatic decisions.
 */
export function loadPersistedState(): {
  state: AppState | null
  error: string | null
} {
  const raw = readRawSafe()
  if (raw === null) return { state: null, error: null }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return failParse()
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !('schemaVersion' in parsed)
  ) {
    return failParse()
  }
  const savedVersion = (parsed as { schemaVersion: unknown }).schemaVersion
  if (typeof savedVersion !== 'number') return failParse()

  // Future-version data: user is on an older build than the data was
  // written by. Treat as parse failure; do NOT downgrade.
  if (savedVersion > CURRENT_SCHEMA_VERSION) return failParse()

  let migrated: unknown = parsed
  if (savedVersion < CURRENT_SCHEMA_VERSION) {
    try {
      migrated = runMigrations(parsed, savedVersion, CURRENT_SCHEMA_VERSION)
    } catch {
      // Tech Arch §12.2: migration failure → treat as parse failure.
      return failParse()
    }
  }

  const result = AppStateSchema.safeParse(migrated)
  if (!result.success) return failParse()

  return { state: result.data, error: null }
}

/**
 * `localStorage.getItem` with a permissive guard. Some restricted
 * contexts (Safari Private mode, sandboxed iframes) throw on access;
 * treat as "no saved data" rather than an error — the user can still
 * use the app with in-memory state.
 */
function readRawSafe(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

function failParse(): { state: null; error: string } {
  setPersistenceError(PARSE_ERROR_MESSAGE)
  return { state: null, error: PARSE_ERROR_MESSAGE }
}

/**
 * Wire the store to localStorage with a debounced (500ms) write-through.
 * Returns a `detach()` callback that unsubscribes the listener AND
 * cancels any pending debounced write — important for test isolation
 * (so a test's pending write doesn't fire into the next test) and
 * future P-022 unmount.
 *
 * Side effect (P-035): updates `pendingSubscribable` (1 on store change,
 * 0 on successful write) and `savedSubscribable` (timestamp on success,
 * null on failure) so the form-UX-polish indicators (`<DirtyAsterisk />`,
 * `<SavedIndicator />`) reflect persistence state without each consumer
 * subscribing to the Store directly.
 */
export function attachPersistence(store: Store): () => void {
  const debouncedWrite = debounce((state: AppState) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state))
      // Successful write clears any prior persistence error (e.g., the
      // user freed space after a quota banner; the next write succeeds
      // and the banner clears). Stamps the saved timestamp + clears the
      // pending flag.
      setPersistenceError(null)
      savedSubscribable.setValue(Date.now())
      pendingSubscribable.setValue(0)
    } catch {
      // QuotaExceededError per Tech Arch §5.3 + any other localStorage
      // write failure (rare — security errors, etc.). Surface the
      // quota banner; the user's recovery path (free space, download
      // recent emails) is the closest match across failure modes.
      setPersistenceError(QUOTA_ERROR_MESSAGE)
      savedSubscribable.setValue(null)
      // Pending stays at 1 — the user has unsaved work. Next successful
      // write resets it.
    }
  }, DEBOUNCE_MS)

  const unsubscribe = store.subscribe((state) => {
    pendingSubscribable.setValue(1)
    debouncedWrite(state)
  })

  return () => {
    unsubscribe()
    debouncedWrite.cancel()
  }
}

/**
 * Reset all persistence streams to their initial values. Test-only
 * helper — production code should never call this. Mirrors the
 * `setPersistenceError(null)` reset already used in test scaffolds
 * (D-083 / D-084), but covers all three streams in one call.
 */
export function resetPersistenceStreamsForTest(): void {
  errorSubscribable.setValue(null)
  savedSubscribable.setValue(null)
  pendingSubscribable.setValue(0)
}
